import React, { useEffect, useContext, useState } from "react";
import {
  Typography,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControl,
} from "@mui/material";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { SelectedExerciseItem } from "./SelectedExerciseItem";
import { AvailableExerciseItem } from "./AvailableExerciseItem";

export const EditProtocolDialog = ({
  open,
  onClose,
  onSave,
  onRemove,
  protocolData,
}) => {
  const { darkMode } = useContext(ThemeContext);
  const { token } = useAuthContext();

  const [protocolName, setProtocolName] = useState("");
  const [renderingExercises, setRenderingExercises] = useState([]);

  useEffect(() => {
    if (!token) return;
    if (protocolData) {
      setProtocolName(protocolData?.protocolname || ""); // Initialize protocol name
      const fetchEBProtocolExercise = async () => {
        try {
          const res1 = await axios.get(
            `${process.env.REACT_APP_API_URL}/ebprotocol/${protocolData?.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const conditionIDs = res1.data.conditions
            .split(",")
            .filter((item) => item)
            .map((item) => parseInt(item, 10));

          const res2 = await axios.post(
            `${process.env.REACT_APP_API_URL}/condition/ids`,
            {
              ids: conditionIDs,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const availableExerciseList = res2.data;

          const rr1 = await axios.post(
            `${process.env.REACT_APP_API_URL}/exercise/ids`,
            {
              ids: availableExerciseList,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const exercisesWithSelected = rr1.data.map((exercise) => ({
            ...exercise,
            selected: false,
          }));

          setRenderingExercises(exercisesWithSelected);

          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/ebprotexercise/protocolid/${protocolData?.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const selectedExerciseData = res.data;

          const exerciseIDs = selectedExerciseData.map(
            (exercise) => exercise.exerciseid
          );

          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/exercise/ids`,
            {
              ids: exerciseIDs,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = selectedExerciseData.map((exercise, index) => ({
            id: response.data[index]?.id,
            name: response.data[index]?.name,
            bmname: response.data[index]?.bmname,
            instructions: exercise.instructions,
            hold: exercise.hold,
            repeat: exercise.repeat,
            timesperday: exercise.timesperday,
            range: exercise.range,
            resistance: exercise.resistance,
            direction: exercise.direction,
            selected: true,
          }));

          setRenderingExercises((prevExercises) =>
            prevExercises.map((exercise) => {
              const selectedExercise = result.find(
                (selectedExercise) => selectedExercise.id === exercise.id
              );
              return selectedExercise
                ? { ...selectedExercise, selected: true } // Use data from selectedExercises
                : { ...exercise, selected: false }; // Keep original data for unselected exercises
            })
          );
        } catch (err) {
          console.error("Error fetching practices:", err);
        }
      };
      fetchEBProtocolExercise();
    }
  }, [protocolData, token]);

  const handleDeleteClicked = () => {
    onRemove(protocolData.id);
  };

  const onAddExercise = (exerciseId) => {
    setRenderingExercises((prevExercises) =>
      prevExercises.map((exercise) =>
        exercise.id === exerciseId ? { ...exercise, selected: true } : exercise
      )
    );
  };

  const onRemoveExercise = async (exerciseId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/exercise/${exerciseId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const exerciseToRemove = response.data;
    setRenderingExercises((prevExercises) =>
      prevExercises.map((exercise) =>
        exercise.id === exerciseId
          ? { ...exerciseToRemove, selected: false }
          : exercise
      )
    );
  };

  const handleSave = () => {
    if (!protocolName.trim()) {
      return;
    }
    const editedProtocolData = {
      ...protocolData,
      protocolname: protocolName,
      exercises: renderingExercises.filter(
        (exercise) => exercise.selected === true
      ),
    };
    onSave(editedProtocolData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <DialogTitle sx={{ fontSize: "30px", pb: 0 }}>Edit Protocol</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <FormControl fullWidth margin="normal">
          <TextField
            label="Protocol Name"
            placeholder="Enter Protocol Name"
            value={protocolName}
            onChange={(e) => setProtocolName(e.target.value)} // Update protocol name state
            error={!protocolName.trim()} // Show error if the input is empty
            helperText={!protocolName.trim() ? "Protocol Name is required" : ""} // Display validation message
          />
        </FormControl>
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: "24px",
            color: darkMode
              ? getColors.drawerTextDark
              : getColors.thumbnailTextLight,
            fontWeight: 500,
            textAlign: "left",
            mt: 3,
            mb: 1,
          }}
        >
          Selected Exercises
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          gap={3}
          rowGap={3}
        >
          {renderingExercises &&
            renderingExercises
              .filter((exercise) => exercise.selected === true)
              .map((exercise, index) => (
                <SelectedExerciseItem
                  key={index}
                  {...exercise}
                  onRemove={() => onRemoveExercise(exercise.id)} // Pass onRemove handler
                  onChange={(updatedExercise) => {
                    setRenderingExercises((prevExercises) =>
                      prevExercises.map((ex) =>
                        ex.id === updatedExercise.id
                          ? { ...ex, ...updatedExercise }
                          : ex
                      )
                    );
                  }}
                />
              ))}
        </Box>
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: "24px",
            color: darkMode
              ? getColors.drawerTextDark
              : getColors.thumbnailTextLight,
            fontWeight: 500,
            textAlign: "left",
            mt: 6,
            mb: 1,
          }}
        >
          Available Exercises
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          gap={3}
          rowGap={3}
        >
          {renderingExercises &&
            renderingExercises
              .filter((exercise) => exercise.selected === false)
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                  sensitivity: "base",
                })
              )
              .map((exercise, index) => (
                <AvailableExerciseItem
                  key={index}
                  {...exercise}
                  onAdd={() => onAddExercise(exercise.id)} // Pass onAdd handler
                />
              ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        <Button
          sx={{
            borderRadius: "5px",
            fontFamily: "Poppins",
            fontSize: "18px",
            fontWeight: 500,
            color: darkMode ? getColors.appBarBgLight : getColors.appBarBgDark,
            px: 6,
            py: "11px",
            textAlign: "center",
            "&: hover": {
              background: "none",
              color: getColors.primaryMain,
            },
            mr: 2,
          }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: "5px",
            fontFamily: "Poppins",
            fontSize: "18px",
            fontWeight: 500,
            px: 6,
            py: "11px",
            textAlign: "center",
            mr: 2,
          }}
          onClick={handleDeleteClicked}
        >
          Delete
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            borderRadius: "5px",
            fontFamily: "Poppins",
            fontSize: "18px",
            fontWeight: 500,
            px: 6,
            py: "11px",
            textAlign: "center",
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
